import { UsageLimitEntitlementName } from '@jupiterone/entitlement-types';
import {
  darkThemeStyles,
  Group,
  IInvitation,
  JupiterTheme,
  useSnackbarNotification,
  EntitlementsUsageTooltip,
  useInviteClient,
} from '@jupiterone/jupiter-web-toolkit';
import { Button, TextField, Typography, WithStyles } from '@material-ui/core';
import { createStyles, makeStyles, withStyles } from '@material-ui/styles';
import React, { ChangeEvent } from 'react';
import { Send } from 'react-feather';
import { useBooleanState } from 'react-use-object-state';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    sendInvitationButton: {
      margin: `${theme.spacing()}px auto 0`,
      '& svg': {
        marginRight: 8,
        height: 18,
        width: 18,
      },
    },
    flexCol: {
      display: 'flex',
      flexFlow: 'column',
      flex: '1 0 auto',
      justifyContent: 'center',
    },
  })
);

interface Props extends WithStyles<typeof darkThemeStyles> {
  classes: any;
  group: Group;
  onUpdateInvitations: (
    invitation: IInvitation,
    action: 'remove' | 'add'
  ) => void;
}

const AddUserTab = ({ group, onUpdateInvitations, ...props }: Props) => {
  const classes = useStyles();
  const notification = useSnackbarNotification();
  const { sendInvite } = useInviteClient();
  const [newUserEmail, setNewUserEmail] = React.useState<string>('');
  const loading = useBooleanState(false);

  const updateUserEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setNewUserEmail(e.target.value);
  };

  const handleSendInvitation = async () => {
    try {
      loading.setTrue();
      const response: Response = await sendInvite(newUserEmail, group.id);
      const json = await response.json();
      if (response.status >= 300) {
        throw new Error(json.error || 'Failed to send invitation');
      }
      const invite: IInvitation = json;
      onUpdateInvitations(invite, 'add');
      notification.success('Successfully sent invitation');
      setNewUserEmail('');
    } catch (err) {
      notification.error({
        message: err.message,
        error: err,
      });
    } finally {
      loading.setFalse();
    }
  };

  return (
    <>
      <Typography
        className={props.classes.tabHeader}
        variant="subtitle1"
        color="primary"
      >
        Add User
      </Typography>
      <TextField
        id="new-user-email"
        label="Email"
        className={props.classes.textField}
        InputLabelProps={{
          className: props.classes.textInputLabel,
          classes: {
            focused: props.classes.textInputLabelFocused,
          },
        }}
        InputProps={{
          className: props.classes.textInput,
          classes: {
            disabled: props.classes.disabled,
            underline: props.classes.textFieldUnderline,
          },
        }}
        margin="normal"
        fullWidth={true}
        value={newUserEmail}
        onChange={updateUserEmail}
      />

      <EntitlementsUsageTooltip
        entitlement={UsageLimitEntitlementName.ACCOUNT_USERS}
        childrenContainerClass={classes.flexCol}
        fallbackTitle="Add user"
        enterDelay={300}
      >
        {(hasEntitlement) => (
          <Button
            className={classes.sendInvitationButton}
            variant="contained"
            color="primary"
            onClick={hasEntitlement ? handleSendInvitation : undefined}
            disabled={loading.state}
          >
            <Send />
            Send Invitation
          </Button>
        )}
      </EntitlementsUsageTooltip>
    </>
  );
};

export default withStyles(darkThemeStyles)(AddUserTab);
export { AddUserTab as AddUserTabWithoutStyles };
